import React, { useEffect, useState } from "react";
import {
  IndianRupee, TrendingUp, ArrowDownCircle, ArrowUpCircle, Boxes,
  ClipboardList, Send, Factory, Users, Package, AlertTriangle, FileText, Wallet, ShoppingBag, FlaskConical, Landmark, Receipt,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, fmtINR, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const StatCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-xl sm:text-2xl font-mono font-semibold tabular-nums text-slate-900 mt-2 break-all">{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-5 h-5" /></div>
    </div>
  </div>
);

const DashSkeleton = () => (
  <div data-testid="dashboard-skeleton" className="space-y-4">
    <div className="ab-skeleton h-9 w-56" />
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="ab-skeleton h-3 w-20" />
          <div className="ab-skeleton h-7 w-24 mt-3" />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 pt-2">
      <div className="xl:col-span-2 ab-skeleton h-72" />
      <div className="ab-skeleton h-72" />
    </div>
  </div>
);

const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [denied, setDenied] = useState(false);
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [pl, setPl] = useState(null);

  const fetchPl = (st, en) => api.get(`/reports/pl/summary?start=${st || ""}&end=${en || ""}`)
    .then((r) => setPl(r.data)).catch(() => setPl(null));
  const fetchStats = (st, en) => { fetchPl(st, en); return api.get(`/dashboard/stats?start=${st || ""}&end=${en || ""}`)
    .then((r) => setS(r.data))
    .catch((err) => { if (err.response?.status === 403) setDenied(true); else setS({}); }); };
  useEffect(() => { fetchStats(start, end); /* eslint-disable-next-line */ }, []);

  if (denied) return <div data-testid="dashboard-no-access" className="text-slate-400 py-20 text-center">You don't have access to the dashboard. Use the sidebar to open a module you have access to.</div>;
  if (!s) return <DashSkeleton />;

  const chartData = [
    { name: "Receivables", amount: s.total_receivables },
    { name: "Payables", amount: s.total_payables },
    { name: "Monthly Sales", amount: s.monthly_sales },
    { name: "Today Sales", amount: s.todays_sales },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Live overview · Aerobent Minechem Private Limited</p>
          {user?.previous_login ? (
            <p data-testid="last-login-info" className="text-xs text-slate-400 mt-1">
              Last sign-in: {new Date(user.previous_login).toLocaleString("en-IN")}
              {user.previous_login_device ? ` · ${user.previous_login_device}` : ""}
              {user.last_login_device && user.previous_login_device && user.last_login_device !== user.previous_login_device && (
                <span data-testid="new-device-flag" className="ml-2 inline-flex items-center gap-1 text-amber-600 font-medium">
                  <AlertTriangle className="w-3 h-3" /> New device this session
                </span>
              )}
            </p>
          ) : (
            <p data-testid="last-login-info" className="text-xs text-slate-400 mt-1">Welcome — this is your first recorded sign-in.</p>
          )}
        </div>
        <div className="flex items-end gap-2 flex-wrap w-full sm:w-auto">
          <div className="space-y-1 flex-1 sm:flex-none min-w-0"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">From</label>
            <Input data-testid="dashboard-start-date" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="bg-white h-9 w-full sm:w-40" /></div>
          <div className="space-y-1 flex-1 sm:flex-none min-w-0"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">To</label>
            <Input data-testid="dashboard-end-date" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-white h-9 w-full sm:w-40" /></div>
          <Button data-testid="dashboard-apply-filter" onClick={() => fetchStats(start, end)} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-md h-9 transition-all active:scale-95">Apply</Button>
        </div>
      </div>

      {s.bank_balance !== undefined && (
        <div className="mb-6">
          {Number(s.low_balance_limit || 0) > 0 && Number(s.bank_balance) < Number(s.low_balance_limit) && (
            <div data-testid="dashboard-low-balance-warning" className="mb-3 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div className="text-sm text-red-800">
                <span className="font-semibold">Low balance warning.</span> Available balance {fmtINR(s.bank_balance)} is below your alert limit of {fmtINR(s.low_balance_limit)}.
              </div>
            </div>
          )}
          <Link to="/bank" data-testid="metric-bank-balance-link" className="block max-w-2xl group">
            <div data-testid="metric-bank-balance" className="bg-[#0B132B] text-white rounded-lg p-5 shadow-sm group-hover:shadow-lg group-hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-white/60">Company Bank Balance</div>
                  <div className="text-2xl sm:text-3xl font-mono font-semibold tabular-nums mt-2 break-all">{fmtINR(s.bank_balance)}</div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs">
                    <span className="text-white/60">Cash: <span className="font-mono text-white" data-testid="metric-bank-cash">{fmtINR(s.bank_balance_cash || 0)}</span></span>
                    <span className="text-white/60">Bank: <span className="font-mono text-white" data-testid="metric-bank-bank">{fmtINR(s.bank_balance_bank || 0)}</span></span>
                  </div>
                  <div className="text-xs text-white/50 mt-2 group-hover:text-white/80">View all transactions &amp; statement →</div>
                </div>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/10 shrink-0"><Landmark className="w-5 h-5" /></div>
              </div>
            </div>
          </Link>
        </div>
      )}

      {pl?.summary && (
        <div className="mb-6" data-testid="dashboard-pl">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Profit &amp; Loss · {fmtDate(start)} — {fmtDate(end)}</h2>
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              {[["Net Revenue", fmtINR(pl.summary.revenue), "text-slate-900", "pl-card-revenue"],
                ["COGS", fmtINR(pl.summary.cogs), "text-slate-900", "pl-card-cogs"],
                ["Gross Profit", fmtINR(pl.summary.gross_profit), "text-emerald-700", "pl-card-gross"],
                ["Operating Expenses", fmtINR(pl.summary.expenses), "text-rose-700", "pl-card-expenses"],
                [Number(pl.summary.net_profit) < 0 ? "Net Loss" : "Net Profit", fmtINR(pl.summary.net_profit),
                  Number(pl.summary.net_profit) < 0 ? "text-red-700" : "text-emerald-700", "pl-card-net"],
                ["Profit Margin", pl.summary.margin_pct === null ? "—" : `${Number(pl.summary.margin_pct).toFixed(2)}%`,
                  Number(pl.summary.net_profit) < 0 ? "text-red-700" : "text-emerald-700", "pl-card-margin"]].map(([l, v, tone, tid]) => (
                <div key={l} data-testid={tid}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{l}</div>
                  <div className={`text-lg font-mono font-semibold tabular-nums mt-1.5 break-all ${tone}`}>{v}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-slate-400">Revenue excludes GST · COGS from actual purchase cost of units sold · Expenses include payroll &amp; wages</p>
              <Link to="/profit-loss" data-testid="view-detailed-pl" className="text-xs font-semibold text-[#0B132B] hover:underline shrink-0">View Detailed P&amp;L →</Link>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6" data-testid="period-summary">        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Period Summary · {fmtDate(start)} — {fmtDate(end)}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard testid="period-sales" icon={Wallet} label="Sales" value={fmtINR(s.period_sales)} accent="bg-emerald-50 text-emerald-600" />
          <StatCard testid="period-purchases" icon={ShoppingBag} label="Purchases" value={fmtINR(s.period_purchases)} accent="bg-rose-50 text-rose-600" />
          <StatCard testid="period-payments" icon={IndianRupee} label="Payments" value={fmtINR(s.period_payments)} accent="bg-emerald-50 text-emerald-600" />
          <StatCard testid="period-receivables" icon={ArrowDownCircle} label="New Receivables" value={fmtINR(s.period_receivables)} accent="bg-sky-50 text-sky-600" />
          <StatCard testid="period-payables" icon={ArrowUpCircle} label="New Payables" value={fmtINR(s.period_payables)} accent="bg-red-50 text-red-600" />
          <StatCard testid="period-production" icon={Factory} label="Production" value={Number(s.period_production || 0).toLocaleString("en-IN")} sub="units" accent="bg-slate-100 text-slate-600" />
          <StatCard testid="period-orders" icon={ClipboardList} label="Orders" value={s.period_orders ?? 0} accent="bg-sky-50 text-sky-600" />
          <StatCard testid="period-dispatch" icon={Send} label="Dispatches" value={s.period_dispatch ?? 0} accent="bg-sky-50 text-sky-600" />
          <StatCard testid="period-samples" icon={FlaskConical} label="Samples" value={s.period_samples ?? 0} accent="bg-slate-100 text-slate-600" />
          <StatCard testid="period-quotations" icon={FileText} label="Quotations" value={s.period_quotations ?? 0} accent="bg-amber-50 text-amber-600" />
        </div>
      </div>

      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Live Snapshot · Today / This Month / Lifetime</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <StatCard testid="metric-todays-sales" icon={IndianRupee} label="Today's Sales" value={fmtINR(s.todays_sales)} accent="bg-sky-50 text-sky-600" />
        <StatCard testid="metric-monthly-sales" icon={TrendingUp} label="Monthly Sales" value={fmtINR(s.monthly_sales)} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="metric-receivables" icon={ArrowDownCircle} label="Outstanding Receivables" value={fmtINR(s.total_receivables)} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="metric-payables" icon={ArrowUpCircle} label="Outstanding Payables" value={fmtINR(s.total_payables)} accent="bg-red-50 text-red-600" />
        {s.unpaid_expenses !== undefined && (
          <Link to="/expenses" data-testid="metric-unpaid-expenses-link" className="block">
            <StatCard testid="metric-unpaid-expenses" icon={Receipt} label="Unpaid Expenses" value={fmtINR(s.unpaid_expenses)} sub={`${s.unpaid_expenses_count || 0} pending · not in balance yet`} accent="bg-amber-50 text-amber-600" />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-4">
        <StatCard testid="metric-total-customers" icon={Users} label="Customers" value={s.total_customers ?? 0} sub={`${s.active_customers ?? 0} active`} accent="bg-sky-50 text-sky-600" />
        <StatCard testid="metric-active-products" icon={Package} label="Active Products" value={s.active_products} accent="bg-slate-100 text-slate-600" />
        <StatCard testid="metric-stock-value" icon={Boxes} label="Stock Value" value={fmtINR(s.stock_value)} accent="bg-slate-100 text-slate-700" />
        <StatCard testid="metric-low-stock" icon={AlertTriangle} label="Low Stock" value={s.low_stock_products} sub="products" accent="bg-amber-50 text-amber-600" />
        <StatCard testid="metric-pending-quotations" icon={FileText} label="Pending Quotes" value={s.pending_quotations} accent="bg-amber-50 text-amber-600" />
        <StatCard testid="metric-todays-production" icon={Factory} label="Today's Production" value={Number(s.todays_production).toLocaleString("en-IN")} sub="units" accent="bg-slate-100 text-slate-600" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard testid="metric-total-sales" icon={Wallet} label="Total Sales" value={fmtINR(s.total_sales)} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="metric-total-purchases" icon={ShoppingBag} label="Total Purchases" value={fmtINR(s.total_purchases)} accent="bg-rose-50 text-rose-600" />
        <StatCard testid="metric-pending-orders" icon={ClipboardList} label="Pending Orders" value={s.pending_orders} accent="bg-amber-50 text-amber-600" />
        <StatCard testid="metric-pending-dispatches" icon={Send} label="Pending Dispatches" value={s.pending_dispatches} accent="bg-sky-50 text-sky-600" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-6" data-testid="sample-kpis">
        <StatCard testid="metric-samples-month" icon={FlaskConical} label="Samples (Month)" value={s.samples_month ?? 0} accent="bg-sky-50 text-sky-600" />
        <StatCard testid="metric-samples-transit" icon={Send} label="Samples In Transit" value={s.samples_in_transit ?? 0} accent="bg-sky-50 text-sky-600" />
        <StatCard testid="metric-samples-delivered" icon={Package} label="Samples Delivered" value={s.samples_delivered ?? 0} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="metric-samples-testing" icon={FlaskConical} label="Under Testing" value={s.samples_testing ?? 0} accent="bg-amber-50 text-amber-600" />
        <StatCard testid="metric-samples-approved" icon={TrendingUp} label="Approved" value={s.samples_approved ?? 0} accent="bg-emerald-50 text-emerald-600" />
        <StatCard testid="metric-samples-rejected" icon={AlertTriangle} label="Rejected" value={s.samples_rejected ?? 0} accent="bg-red-50 text-red-600" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-800 font-head mb-4">Financial Snapshot</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0B132B", border: "none", borderRadius: 4, color: "#fff", fontFamily: "JetBrains Mono" }} formatter={(v) => fmtINR(v)} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="amount" fill="#1C2541" radius={[3, 3, 0, 0]} maxBarSize={64} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-800 font-head mb-4">Recent Transactions</h2>
          <div className="space-y-2" data-testid="recent-transactions">
            {s.recent_transactions.length === 0 && <p className="text-sm text-slate-400">No transactions yet.</p>}
            {s.recent_transactions.map((t, i) => (
              <div key={i} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{t.party}</div>
                  <div className="text-xs text-slate-400">{t.type} · {t.ref} · {fmtDate(t.date)}</div>
                </div>
                <div className={`text-sm font-mono tabular-nums font-semibold shrink-0 ml-2 ${t.type === "Payment" ? "text-emerald-600" : "text-slate-900"}`}>{fmtINR(t.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
